import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Image as ImageIcon, Plus, X } from 'lucide-react';
import {
  InclusionItem,
  InclusionField,
  normalizeInclusionList,
  serializeInclusions,
} from '../inclusionItem';

interface InclusionListEditorProps {
  label: string;
  helperText?: string;
  placeholder?: string;
  /** Maximum number of items (default 10) */
  max?: number;
  /** Current value — accepts legacy string[] or rich InclusionItem[]. */
  value: InclusionField;
  /** Receives the persisted shape (string[] when no rich data, InclusionItem[] when icons/descriptions are present). */
  onChange: (next: string[] | InclusionItem[]) => void;
  /** Optional callback to open the shared image picker for icon selection. Receives a setter that the picker should invoke with the chosen URL. */
  onPickIcon?: (apply: (url: string) => void) => void;
  /** Optional URL resolver (e.g. SAS) for displaying icons. */
  getCachedImageUrl?: (url: string | undefined) => string | undefined;
}

export function InclusionListEditor({
  label,
  helperText,
  placeholder,
  max = 10,
  value,
  onChange,
  onPickIcon,
  getCachedImageUrl,
}: InclusionListEditorProps) {
  const items: InclusionItem[] = normalizeInclusionList(value);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const update = (next: InclusionItem[]) => onChange(serializeInclusions(next));

  const addRow = () => {
    if (items.length >= max) return;
    update([...items, { text: '' }]);
    setExpandedIdx(items.length);
  };

  const removeRow = (i: number) => {
    update(items.filter((_, idx) => idx !== i));
    if (expandedIdx === i) setExpandedIdx(null);
  };

  const patchRow = (i: number, patch: Partial<InclusionItem>) =>
    update(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
      <div className="flex items-center justify-between mb-2">
        <div>
          <label className="block text-xs font-medium text-gray-700">{label}</label>
          {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
        </div>
        <button
          type="button"
          onClick={addRow}
          disabled={items.length >= max}
          className={`px-2 py-1 rounded text-xs font-medium transition-colors inline-flex items-center gap-1 ${
            items.length >= max
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <Plus className="w-3 h-3" />
          Add
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-xs text-gray-400 text-center py-2 border border-dashed border-gray-300 rounded bg-white">
          No items yet. Click "Add" to create one.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item, idx) => {
            const isExpanded = expandedIdx === idx;
            const iconUrl = getCachedImageUrl ? getCachedImageUrl(item.icon) : item.icon;
            return (
              <div key={idx} className="bg-white rounded border border-gray-200">
                <div className="flex items-center gap-2 p-2">
                  {/* Icon slot */}
                  <button
                    type="button"
                    onClick={() => onPickIcon?.((url) => patchRow(idx, { icon: url }))}
                    disabled={!onPickIcon}
                    className={`w-8 h-8 flex-shrink-0 rounded border bg-gray-50 flex items-center justify-center overflow-hidden transition-colors ${
                      onPickIcon ? 'border-gray-200 hover:border-blue-400 hover:bg-blue-50 cursor-pointer' : 'border-gray-200 cursor-default opacity-60'
                    }`}
                    title={onPickIcon ? 'Choose icon' : 'Icon picker unavailable'}
                  >
                    {iconUrl ? (
                      <img src={iconUrl} alt="" className="w-7 h-7 object-contain" />
                    ) : (
                      <ImageIcon className="w-4 h-4 text-gray-300" />
                    )}
                  </button>

                  {/* Text */}
                  <input
                    type="text"
                    value={item.text}
                    onChange={(e) => patchRow(idx, { text: e.target.value })}
                    onFocus={() => setExpandedIdx(idx)}
                    placeholder={placeholder}
                    className="flex-1 rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />

                  {/* Expand toggle */}
                  <button
                    type="button"
                    onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                    className="p-1 text-gray-400 hover:text-gray-700 rounded transition-colors"
                    title={isExpanded ? 'Hide description' : 'Add description'}
                    aria-label={isExpanded ? 'Hide description' : 'Add description'}
                  >
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>

                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Remove item"
                    aria-label="Remove item"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {isExpanded && (
                  <div className="px-2 pb-2 pt-0">
                    <textarea
                      value={item.description ?? ''}
                      onChange={(e) => patchRow(idx, { description: e.target.value })}
                      rows={2}
                      placeholder="Optional description (shown beneath the item in the customer view)"
                      className="w-full rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    {item.icon && (
                      <button
                        type="button"
                        onClick={() => patchRow(idx, { icon: undefined })}
                        className="mt-1 text-[11px] text-gray-500 hover:text-red-500"
                      >
                        Remove icon
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {items.length >= max && (
        <p className="text-[11px] text-gray-400 mt-1">Maximum of {max} items reached.</p>
      )}
    </div>
  );
}
